import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { MaskLines, Reveal } from "../components/site/Reveal";
import { CtaBand } from "../components/site/Sections";
import { Seo } from "../components/site/Seo";
import { api, API_BASE } from "../lib/api";
import { formatDate } from "./Blog";

const Body = ({ text }) => (
  <div className="space-y-6" data-testid="blog-post-body">
    {(text || "").split(/\n{2,}|\n(?=## )/).map((block, i) => {
      const t = block.trim();
      if (!t) return null;
      if (t.startsWith("## ")) {
        const [head, ...rest] = t.split("\n");
        return (
          <div key={i}>
            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">{head.slice(3)}</h2>
            {rest.length > 0 && <p className="mt-3 text-lg leading-relaxed text-ink/80">{rest.join(" ")}</p>}
          </div>
        );
      }
      return <p key={i} className="text-lg leading-relaxed text-ink/80">{t}</p>;
    })}
  </div>
);

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!API_BASE) { setState("error"); return undefined; }
    setState("loading");
    api.get(`/posts/${slug}`).then((r) => { setPost(r.data); setState("ok"); }).catch(() => setState("error"));
    return undefined;
  }, [slug]);

  if (state === "loading") return <div className="wrap min-h-[60vh] pt-24"><div className="h-16 w-2/3 animate-pulse rounded-xl bg-muted" /></div>;
  if (state === "error" || !post) {
    return (
      <div className="wrap min-h-[60vh] pt-24" data-testid="blog-post-missing">
        <h1 className="font-display text-4xl font-bold text-ink">Article not found</h1>
        <Link to="/blog" className="btn-ghost mt-8">Back to the blog</Link>
      </div>
    );
  }

  return (
    <>
      <Seo path={`/blog/${post.slug}`} title={post.title} description={post.excerpt} />
      <article>
        <header className="wrap max-w-4xl pb-12 pt-16 sm:pt-24">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-flame" data-testid="back-to-blog"><ArrowLeft className="h-4 w-4" /> All articles</Link>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-ink/65">
            <span className="rounded-full bg-flame/10 px-3 py-1 font-semibold text-flame-dark">{post.category}</span>
            <span>{formatDate(post.created_at)}</span><span>·</span><span>{post.read_time}</span><span>·</span><span>{post.author}</span>
          </div>
          <MaskLines lines={[post.title]} className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl" testId="blog-post-title" />
        </header>
        <Reveal className="wrap max-w-5xl">
          <div className="aspect-[16/9] overflow-hidden rounded-[1.75rem] bg-muted"><img src={post.cover_image} alt="" className="h-full w-full object-cover" /></div>
        </Reveal>
        <div className="wrap max-w-3xl py-16 sm:py-20">
          <p className="mb-10 font-display text-xl font-medium leading-snug text-ink sm:text-2xl">{post.excerpt}</p>
          <Body text={post.body} />
        </div>
      </article>
      <CtaBand />
    </>
  );
}
